
/** Column-major 4x4, in the order WebGL's `uniformMatrix4fv` wants it. */
export type Mat4 = Float32Array;

export const DEFAULT_PITCH_DEG = 27;

export const DEFAULT_YAW_DEG = 0;

export const FOV_DEG = 30;

export const NEAR_MM = 1_000;
export const FAR_MM = 400_000;

const DEG = Math.PI / 180;

export function identity(): Mat4 {
  const m = new Float32Array(16);
  m[0] = m[5] = m[10] = m[15] = 1;
  return m;
}

/** `out = a * b`, both column-major. Written out rather than looped: this runs
 *  once a frame and the unrolled form is the one that can be read against a
 *  reference. */
export function multiply(a: Mat4, b: Mat4, out: Mat4 = new Float32Array(16)): Mat4 {
  for (let column = 0; column < 4; column++) {
    const b0 = b[column * 4];
    const b1 = b[column * 4 + 1];
    const b2 = b[column * 4 + 2];
    const b3 = b[column * 4 + 3];
    for (let row = 0; row < 4; row++) {
      out[column * 4 + row] =
        a[row] * b0 + a[4 + row] * b1 + a[8 + row] * b2 + a[12 + row] * b3;
    }
  }
  return out;
}

/** A right-handed perspective projection, WebGL clip conventions. */
export function perspective(
  fovYRad: number,
  aspect: number,
  near: number,
  far: number,
  out: Mat4 = new Float32Array(16),
): Mat4 {
  const f = 1 / Math.tan(fovYRad / 2);
  out.fill(0);
  out[0] = f / aspect;
  out[5] = f;
  out[10] = (far + near) / (near - far);
  out[11] = -1;
  out[14] = (2 * far * near) / (near - far);
  return out;
}

/** A right-handed look-at, with `up` assumed non-parallel to the view direction. */
export function lookAt(
  eye: readonly [number, number, number],
  centre: readonly [number, number, number],
  up: readonly [number, number, number],
  out: Mat4 = new Float32Array(16),
): Mat4 {
  const [ex, ey, ez] = eye;
  let zx = ex - centre[0];
  let zy = ey - centre[1];
  let zz = ez - centre[2];
  const zLen = Math.hypot(zx, zy, zz) || 1;
  zx /= zLen;
  zy /= zLen;
  zz /= zLen;

  let xx = up[1] * zz - up[2] * zy;
  let xy = up[2] * zx - up[0] * zz;
  let xz = up[0] * zy - up[1] * zx;
  const xLen = Math.hypot(xx, xy, xz) || 1;
  xx /= xLen;
  xy /= xLen;
  xz /= xLen;

  const yx = zy * xz - zz * xy;
  const yy = zz * xx - zx * xz;
  const yz = zx * xy - zy * xx;

  out[0] = xx;
  out[1] = yx;
  out[2] = zx;
  out[3] = 0;
  out[4] = xy;
  out[5] = yy;
  out[6] = zy;
  out[7] = 0;
  out[8] = xz;
  out[9] = yz;
  out[10] = zz;
  out[11] = 0;
  out[12] = -(xx * ex + xy * ey + xz * ez);
  out[13] = -(yx * ex + yy * ey + yz * ez);
  out[14] = -(zx * ex + zy * ey + zz * ez);
  out[15] = 1;
  return out;
}

export interface ViewOptions {
  /** Where the camera is looking, in world millimetres. */
  readonly targetXMm: number;
  readonly targetYMm: number;
  /** The ground height under the target. Keeps the camera from sinking into a
   *  hill the player is standing on. */
  readonly targetZMm: number;
  readonly viewportWidth: number;
  readonly viewportHeight: number;
  readonly mmPerPixel: number;
  readonly pitchDeg?: number;
  readonly yawDeg?: number;
  readonly fovDeg?: number;
}

export function eyeDistanceMm(
  mmPerPixel: number,
  viewportHeight: number,
  _pitchDeg: number = DEFAULT_PITCH_DEG,
  fovDeg: number = FOV_DEG,
): number {
  const spanMm = Math.max(1, viewportHeight) * mmPerPixel;
  return spanMm / (2 * Math.tan((fovDeg * DEG) / 2));
}

/** Everything the renderer needs for one frame, computed once. */
export class View {
  readonly viewProj: Mat4 = identity();
  /** Where the eye is, for anything that needs a direction to the camera --
   *  billboards, and the fog that fades the far edge of the interest radius. */
  eyeXMm = 0;
  eyeYMm = 0;
  eyeZMm = 0;
  private viewportWidth = 1;
  private viewportHeight = 1;

  private readonly projection: Mat4 = identity();
  private readonly camera: Mat4 = identity();

  get billboardRight(): readonly [number, number, number] {
    const m = this.camera;
    return [m[0], m[4], m[8]];
  }

  /** The view matrix alone, without the projection. What the original hands
   *  `D3DTSS_TCI_CAMERASPACEPOSITION` to work in
   *  (`GameLib/MapOutdoorRender.cpp:627`): a shader that needs a vertex's
   *  position relative to the eye needs this and not `viewProj`. */
  get view(): Mat4 {
    return this.camera;
  }

  update(options: ViewOptions): void {
    const pitch = (options.pitchDeg ?? DEFAULT_PITCH_DEG) * DEG;
    const yaw = (options.yawDeg ?? DEFAULT_YAW_DEG) * DEG;
    const fov = (options.fovDeg ?? FOV_DEG) * DEG;
    const width = Math.max(1, options.viewportWidth);
    const height = Math.max(1, options.viewportHeight);
    this.viewportWidth = width;
    this.viewportHeight = height;

    const distance = eyeDistanceMm(
      options.mmPerPixel,
      height,
      options.pitchDeg ?? DEFAULT_PITCH_DEG,
      options.fovDeg ?? FOV_DEG,
    );

    const groundReach = Math.cos(pitch) * distance;
    this.eyeXMm = options.targetXMm - Math.sin(yaw) * groundReach;
    this.eyeYMm = options.targetYMm - Math.cos(yaw) * groundReach;
    this.eyeZMm = options.targetZMm + Math.sin(pitch) * distance;

    perspective(fov, width / height, NEAR_MM, FAR_MM, this.projection);
    lookAt(
      [this.eyeXMm, this.eyeYMm, this.eyeZMm],
      [options.targetXMm, options.targetYMm, options.targetZMm],
      [0, 0, 1],
      this.camera,
    );
    multiply(this.projection, this.camera, this.viewProj);
  }

  project(xMm: number, yMm: number, zMm = 0): {
    x: number;
    y: number;
    depth: number;
    behind: boolean;
    /** Pixels per millimetre at this point, for sizing a billboard. */
    scale: number;
  } {
    const m = this.viewProj;
    const clipX = m[0] * xMm + m[4] * yMm + m[8] * zMm + m[12];
    const clipY = m[1] * xMm + m[5] * yMm + m[9] * zMm + m[13];
    const clipZ = m[2] * xMm + m[6] * yMm + m[10] * zMm + m[14];
    const clipW = m[3] * xMm + m[7] * yMm + m[11] * zMm + m[15];

    if (clipW <= 0) {
      return { x: 0, y: 0, depth: Number.POSITIVE_INFINITY, behind: true, scale: 0 };
    }

    const ndcX = clipX / clipW;
    const ndcY = clipY / clipW;
    return {
      x: (ndcX * 0.5 + 0.5) * this.viewportWidth,
      y: (0.5 - ndcY * 0.5) * this.viewportHeight,
      depth: clipZ / clipW,
      behind: false,
      // From the projection's own vertical scale: half the viewport in pixels,
      // times the matrix's y term, divided by the depth. Derived rather than
      // assumed constant, because under perspective it is not.
      scale: (this.viewportHeight * 0.5 * m[5]) / clipW,
    };
  }

  unproject(px: number, py: number, groundZMm = 0): { xMm: number; yMm: number } | null {
    const ndcX = (px / this.viewportWidth) * 2 - 1;
    const ndcY = 1 - (py / this.viewportHeight) * 2;

    const inverse = invert(this.viewProj);
    if (inverse === null) return null;

    const near = transformPoint(inverse, ndcX, ndcY, -1);
    const far = transformPoint(inverse, ndcX, ndcY, 1);
    if (near === null || far === null) return null;

    const dz = far[2] - near[2];
    if (Math.abs(dz) < 1e-6) return null;
    const t = (groundZMm - near[2]) / dz;
    if (t < 0) return null;

    return {
      xMm: near[0] + (far[0] - near[0]) * t,
      yMm: near[1] + (far[1] - near[1]) * t,
    };
  }
}

function transformPoint(
  m: Mat4,
  x: number,
  y: number,
  z: number,
): [number, number, number] | null {
  const w = m[3] * x + m[7] * y + m[11] * z + m[15];
  if (Math.abs(w) < 1e-9) return null;
  return [
    (m[0] * x + m[4] * y + m[8] * z + m[12]) / w,
    (m[1] * x + m[5] * y + m[9] * z + m[13]) / w,
    (m[2] * x + m[6] * y + m[10] * z + m[14]) / w,
  ];
}

/** A general 4x4 inverse. Only `unproject` needs it, once per pick, so the
 *  cofactor expansion's cost is irrelevant and its readability is not. */
export function invert(m: Mat4): Mat4 | null {
  const a00 = m[0], a01 = m[1], a02 = m[2], a03 = m[3];
  const a10 = m[4], a11 = m[5], a12 = m[6], a13 = m[7];
  const a20 = m[8], a21 = m[9], a22 = m[10], a23 = m[11];
  const a30 = m[12], a31 = m[13], a32 = m[14], a33 = m[15];

  const b00 = a00 * a11 - a01 * a10;
  const b01 = a00 * a12 - a02 * a10;
  const b02 = a00 * a13 - a03 * a10;
  const b03 = a01 * a12 - a02 * a11;
  const b04 = a01 * a13 - a03 * a11;
  const b05 = a02 * a13 - a03 * a12;
  const b06 = a20 * a31 - a21 * a30;
  const b07 = a20 * a32 - a22 * a30;
  const b08 = a20 * a33 - a23 * a30;
  const b09 = a21 * a32 - a22 * a31;
  const b10 = a21 * a33 - a23 * a31;
  const b11 = a22 * a33 - a23 * a32;

  const det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;
  if (Math.abs(det) < 1e-12) return null;
  const d = 1 / det;

  const out = new Float32Array(16);
  out[0] = (a11 * b11 - a12 * b10 + a13 * b09) * d;
  out[1] = (a02 * b10 - a01 * b11 - a03 * b09) * d;
  out[2] = (a31 * b05 - a32 * b04 + a33 * b03) * d;
  out[3] = (a22 * b04 - a21 * b05 - a23 * b03) * d;
  out[4] = (a12 * b08 - a10 * b11 - a13 * b07) * d;
  out[5] = (a00 * b11 - a02 * b08 + a03 * b07) * d;
  out[6] = (a32 * b02 - a30 * b05 - a33 * b01) * d;
  out[7] = (a20 * b05 - a22 * b02 + a23 * b01) * d;
  out[8] = (a10 * b10 - a11 * b08 + a13 * b06) * d;
  out[9] = (a01 * b08 - a00 * b10 - a03 * b06) * d;
  out[10] = (a30 * b04 - a31 * b02 + a33 * b00) * d;
  out[11] = (a21 * b02 - a20 * b04 - a23 * b00) * d;
  out[12] = (a11 * b07 - a10 * b09 - a12 * b06) * d;
  out[13] = (a00 * b09 - a01 * b07 + a02 * b06) * d;
  out[14] = (a31 * b01 - a30 * b03 - a32 * b00) * d;
  out[15] = (a20 * b03 - a21 * b01 + a22 * b00) * d;
  return out;
}
